import type { FC, ReactNode } from 'react';
import { useEffect } from 'react';
import { LuX, LuArrowLeft } from 'react-icons/lu';
import './RecordPanel.css';

export interface PanelTab {
    id: string;
    label: string;
    icon?: ReactNode;
    /** Contagem à direita do rótulo, tipo "Permissões 12". */
    badge?: ReactNode;
}

interface RecordPanelProps {
    open: boolean;
    onClose: () => void;
    /** Iniciais no avatar. Omitir esconde o avatar. */
    initials?: string;
    /**
     * Avatar quadrado em vez de redondo. É o que separa EMPRESA de PESSOA
     * sem precisar de rótulo: organização é quadrada, gente é redonda.
     * Ver docs/propostas/organizacoes-fase-1.html §6.
     */
    squareAvatar?: boolean;
    title: string;
    /** Linha de apoio. No modo edição vira "Editando…" por conta do componente. */
    subtitle?: string;
    /** true = modo edição. O cabeçalho NÃO muda: só a linha de apoio e o rodapé. */
    editing?: boolean;
    /**
     * Volta do modo edição para o modo ver. Quando vem, o cabeçalho ganha uma
     * seta à esquerda no modo edição — antes só dava para sair por "Cancelar"
     * lá embaixo, e ele apontou que não estava claro (2026-09-12). O "X"
     * continua fechando o painel inteiro: são saídas diferentes.
     *
     * Sem esta prop nada muda, para não alterar quem já usa o componente.
     */
    onBack?: () => void;
    /**
     * Barra de ações — só aparece no modo ver.
     *
     * **Não usar junto com `tabs`.** Um botão acima das abas promete escopo
     * que ele não tem: editar "Dados" não é editar "Pessoas". Em painel com
     * abas o botão de editar vai DENTRO do conteúdo da aba, montado pela
     * página. Ver `padrao-de-tela.md`.
     */
    actions?: ReactNode;
    /** Rodapé — só aparece no modo edição. */
    footer?: ReactNode;
    /**
     * Rodapé também fora do modo edição. Para fluxo que não é registro (a
     * importação de planilha, 04/10/2026): tem botões de avançar e voltar, mas
     * "Editando…" na linha de apoio seria mentira. Sem a prop, nada muda.
     */
    persistentFooter?: boolean;
    /**
     * Abas DENTRO do painel, quando o registro tem dois assuntos grandes
     * demais para empilhar (perfil: dados gerais × permissões). Continuam
     * sendo um painel só: a aba troca o conteúdo, não abre outro painel.
     */
    tabs?: PanelTab[];
    activeTab?: string;
    onTabChange?: (id: string) => void;
    /** Painel largo (680px), para conteúdo que não cabe bem em 460. */
    wide?: boolean;
    /**
     * Painel extra largo (1080px), para LISTA com várias colunas — a lista do que um número
     * do Painel Operacional contou (17/10/2026). Vence o `wide` quando os dois vêm.
     */
    extraWide?: boolean;
    children: ReactNode;
}

/**
 * Painel lateral de registro, com DOIS MODOS no mesmo painel.
 *
 * Regra do produto, corrigida três vezes antes de virar regra: detalhe e
 * formulário abrem no MESMO painel, que troca de conteúdo — nunca um drawer
 * por cima de outro, nunca modal centralizada. A linha da lista abre em
 * `ver`; "Editar" troca para `editing`. O cabeçalho continua igual (mesmo
 * avatar, mesmo nome), só a linha de apoio vira "Editando…". "Novo" usa este
 * mesmo painel já em modo edição.
 *
 * Ver docs/designsystem/estado-do-redesign.md §5.
 */
const RecordPanel: FC<RecordPanelProps> = ({
    open, onClose, initials, squareAvatar = false, title, subtitle, editing = false, onBack, actions, footer,
    persistentFooter = false, tabs, activeTab, onTabChange, wide = false, extraWide = false, children,
}) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="rp-overlay" onClick={onClose}>
            <aside
                className={`rp ${extraWide ? 'is-xwide' : wide ? 'is-wide' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={e => e.stopPropagation()}
            >
                <header className="rp-head">
                    {editing && onBack && (
                        <button type="button" className="rp-back" onClick={onBack} aria-label="Voltar para os detalhes">
                            <LuArrowLeft size={15} />
                        </button>
                    )}
                    {initials && <span className={`rp-avatar ${squareAvatar ? 'is-square' : ''}`} aria-hidden="true">{initials}</span>}
                    <div className="rp-id">
                        <h2 className="rp-title">{title}</h2>
                        {editing
                            ? <p className="rp-sub is-editing">Editando…</p>
                            : subtitle && <p className="rp-sub">{subtitle}</p>}
                    </div>
                    <button type="button" className="rp-close" onClick={onClose} aria-label="Fechar">
                        <LuX size={14} />
                    </button>
                </header>

                {!editing && actions && <div className="rp-actions">{actions}</div>}

                {tabs && tabs.length > 0 && (
                    <nav className="rp-tabs" role="tablist">
                        {tabs.map(t => (
                            <button
                                key={t.id}
                                type="button"
                                role="tab"
                                aria-selected={activeTab === t.id}
                                className={`rp-tab ${activeTab === t.id ? 'is-active' : ''}`}
                                onClick={() => onTabChange?.(t.id)}
                            >
                                {t.icon}
                                <span>{t.label}</span>
                                {t.badge !== undefined && <span className="rp-tab-badge">{t.badge}</span>}
                            </button>
                        ))}
                    </nav>
                )}

                <div className="rp-body">{children}</div>

                {(editing || persistentFooter) && footer && <div className="rp-foot">{footer}</div>}
            </aside>
        </div>
    );
};

/** Bloco do painel, com rótulo em caixa alta. */
interface SwitchProps {
    checked: boolean;
    onChange: (v: boolean) => void;
    /** Vira o `aria-label`: o interruptor não tem texto próprio. */
    label: string;
    disabled?: boolean;
}

/**
 * O interruptor do painel.
 *
 * **Existia em duas formas no app, e uma delas nunca funcionou.** O CSS
 * (`.rp-switch`) sempre foi desenhado para `<label>` + `<input type=checkbox>`,
 * com o estado saindo de `input:checked ~ .rp-switch-track`. Cinco lugares
 * tinham sido escritos como `<button className="rp-switch is-on">`, e
 * `.rp-switch.is-on` **não existe em lugar nenhum** — então:
 *
 * - o estado alternava no React e a tela não mudava (parecia travado);
 * - e o `<button>` aparecia com a borda `2px outset` do navegador, porque
 *   `.rp-switch` posiciona mas não zera o estilo nativo — era o "retângulo em
 *   volta" que ele viu em Campos personalizados (2026-09-13).
 *
 * Agora é uma peça só, e a forma errada não tem como ser escrita de novo.
 */
export const Switch: FC<SwitchProps> = ({ checked, onChange, label, disabled = false }) => (
    <label className="rp-switch">
        <input
            type="checkbox"
            role="switch"
            checked={checked}
            disabled={disabled}
            aria-label={label}
            onChange={e => onChange(e.target.checked)}
        />
        <span className="rp-switch-track" />
        <span className="rp-switch-thumb" />
    </label>
);

export const PanelSection: FC<{ title: string; extra?: ReactNode; children: ReactNode }> = ({
    title, extra, children,
}) => (
    <section className="rp-sec">
        <div className="rp-sec-head">
            <h3 className="rp-sec-title">{title}</h3>
            {extra}
        </div>
        {children}
    </section>
);

export default RecordPanel;
